import {
  Button,
  ErrorState,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TOUCH,
} from "@nymbl/nos2-rules";
import { X } from "lucide-react";
import { SIGNOFF_CELL } from "@/components/team/team-colors";
import {
  SIGNOFF_STATE_LABELS,
  type SignOffCellState,
  type SignOffGrid,
} from "@/lib/team/signoff-grid";
import { cn } from "@/lib/utils";

const STATES: SignOffCellState[] = ["onTime", "late", "overdue", "open"];

interface SignOffsPageProps {
  onClose: () => void;
  grid: SignOffGrid | null;
  isError: boolean;
  onRetry: () => void;
}

/** Person x period sign-off status (DECISIONS 2026-10-15); read only, details on hover. */
export function SignOffsPage({ onClose, grid, isError, onRetry }: SignOffsPageProps) {
  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title="Time sign-offs"
        actions={
          <Button variant="outline" className={TOUCH} onClick={onClose}>
            <X aria-hidden="true" />
            Close
          </Button>
        }
      />
      <ul className="flex flex-wrap gap-3 text-sm" aria-label="Legend">
        {STATES.map((s) => (
          <li key={s} className="flex items-center gap-1.5">
            <span className={cn("size-3 rounded-sm", SIGNOFF_CELL[s])} aria-hidden="true" />
            {SIGNOFF_STATE_LABELS[s]}
          </li>
        ))}
      </ul>
      {isError && <ErrorState message="Sign-offs didn't load" onRetry={onRetry} />}
      {!grid && !isError && <Skeleton className="h-40 w-full" />}
      {grid && (
        <Table aria-label="Sign-offs by person" scrollRegion>
          <TableHeader>
            <TableRow>
              <TableHead>Person</TableHead>
              {grid.columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={cn("text-center", c.periodType === "month" && "font-bold")}
                >
                  {c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {grid.rows.map((row) => (
              <TableRow key={row.personId}>
                <TableHead scope="row" className="font-semibold">
                  {row.personName}
                </TableHead>
                {row.cells.map((cell, i) => (
                  <TableCell key={grid.columns[i]?.key ?? row.personId} className="text-center">
                    {cell ? (
                      <div
                        title={cell.title}
                        aria-label={cell.title}
                        role="img"
                        className={cn(
                          "rounded px-2 py-1 text-xs font-semibold tabular-nums",
                          SIGNOFF_CELL[cell.state],
                        )}
                      >
                        {cell.text}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
